// modules/asset/frontend/pages/AssetList.tsx
//
// Asset list view — first module-owned dashboard page (G #615) +
// first consumer of the @nexus/frontend-shared workspace
// (post-G frontend-shared #1).
//
// Upgraded from the raw-fetch stub of G:
//   - apiFetch (session-refresh on 401, correlation id, plan correlation)
//   - PageHeader for title + description
//   - Skeleton for loading state
//   - EmptyState for "no assets yet"
//
// Still not in this PR (intentional):
//   - DataTable / PaginationControl — shared workspace doesn't expose
//     these yet (they're tied to the dashboard's session context).
//     AssetList still uses a hand-rolled <table>. Future PR promotes
//     DataTable once a second module page needs it.
//   - ConfirmDialog — list view has no destructive actions. Asset E-2
//     (edit / delete) brings the need + the promotion.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Boxes } from "lucide-react";
import { apiFetch } from "@nexus/frontend-shared/api-client";
import PageHeader from "@nexus/frontend-shared/components/PageHeader";
import Skeleton from "@nexus/frontend-shared/components/Skeleton";
import EmptyState from "@nexus/frontend-shared/components/EmptyState";

interface AssetRow {
  id: string;
  tenant_id: string;
  site_id: string | null;
  name: string;
  kind: string;
  status: "active" | "retired" | "missing";
  description: string | null;
  created_at: string;
  updated_at: string;
}

const STATUS_LABEL: Record<AssetRow["status"], string> = {
  active: "啟用",
  retired: "已退役",
  missing: "失蹤",
};

const STATUS_BADGE: Record<AssetRow["status"], string> = {
  active: "bg-success/15 text-success",
  retired: "bg-text-muted/15 text-text-muted",
  missing: "bg-danger/15 text-danger",
};

type ListState =
  | { kind: "loading" }
  | { kind: "ready"; rows: AssetRow[]; total: number }
  | { kind: "error"; message: string };

export default function AssetList(): React.ReactElement {
  const nav = useNavigate();
  const [state, setState] = useState<ListState>({ kind: "loading" });
  const [kindFilter, setKindFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setState({ kind: "loading" });
      try {
        const qs = new URLSearchParams();
        if (kindFilter) qs.set("kind", kindFilter);
        if (statusFilter) qs.set("status", statusFilter);
        const r = await apiFetch(`/v1/assets${qs.toString() ? `?${qs}` : ""}`);
        if (!r.ok) {
          if (cancelled) return;
          setState({ kind: "error", message: `HTTP ${r.status}` });
          return;
        }
        const body = (await r.json()) as { items: AssetRow[]; total: number };
        if (cancelled) return;
        setState({ kind: "ready", rows: body.items, total: body.total });
      } catch (err) {
        if (cancelled) return;
        setState({
          kind: "error",
          message: err instanceof Error ? err.message : String(err),
        });
      }
    })();
    return () => { cancelled = true; };
  }, [kindFilter, statusFilter]);

  const headerStats = useMemo(() => {
    if (state.kind !== "ready") return null;
    return state.rows.reduce<Record<string, number>>((acc, r) => {
      acc[r.status] = (acc[r.status] ?? 0) + 1;
      return acc;
    }, {});
  }, [state]);

  return (
    <div>
      <PageHeader
        title="資產盤點"
        description="案場資產登錄、檢查與盤點。AI 助手可以 list / create 資產（透過 chat）；完整 CRUD 介面在後續 PR。"
        actions={
          headerStats ? (
            <div className="flex items-center gap-3 text-2xs text-text-muted">
              {(["active", "retired", "missing"] as const).map((s) => (
                <span key={s} className="whitespace-nowrap">
                  {STATUS_LABEL[s]} {headerStats[s] ?? 0}
                </span>
              ))}
            </div>
          ) : undefined
        }
      />

      <div className="flex flex-wrap items-center gap-3 text-2xs mb-4">
        <label className="flex items-center gap-2">
          <span className="text-text-muted">種類</span>
          <input
            type="text"
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value)}
            placeholder="如 fire_extinguisher"
            className="px-2 py-1 rounded border border-border bg-bg-surface text-text-primary focus:ring-1 focus:ring-accent focus:outline-none"
          />
        </label>
        <label className="flex items-center gap-2">
          <span className="text-text-muted">狀態</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-2 py-1 rounded border border-border bg-bg-surface text-text-primary focus:ring-1 focus:ring-accent focus:outline-none"
          >
            <option value="">全部</option>
            <option value="active">{STATUS_LABEL.active}</option>
            <option value="retired">{STATUS_LABEL.retired}</option>
            <option value="missing">{STATUS_LABEL.missing}</option>
          </select>
        </label>
      </div>

      {state.kind === "loading" && (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      )}

      {state.kind === "error" && (
        <div className="rounded-xl border border-danger/30 bg-danger/5 px-4 py-3 text-2xs text-danger">
          載入失敗：{state.message}
        </div>
      )}

      {state.kind === "ready" && state.rows.length === 0 && (
        <EmptyState
          icon={Boxes}
          title="尚未登錄任何資產"
          description='可以請 AI 助手「幫我新增一筆 fire_extinguisher 滅火器到 X 案場」。'
        />
      )}

      {state.kind === "ready" && state.rows.length > 0 && (
        <div className="rounded-xl border border-border bg-bg-surface overflow-hidden">
          <table className="w-full text-2xs">
            <thead className="bg-bg-base text-text-muted">
              <tr>
                <th className="px-4 py-2 text-left font-medium">名稱</th>
                <th className="px-4 py-2 text-left font-medium">種類</th>
                <th className="px-4 py-2 text-left font-medium">狀態</th>
                <th className="px-4 py-2 text-left font-medium">案場</th>
                <th className="px-4 py-2 text-left font-medium">建立時間</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {state.rows.map((row) => (
                <tr
                  key={row.id}
                  className="text-text-primary cursor-pointer hover:bg-bg-hover transition-colors"
                  onClick={() => nav(`/dashboard/assets/${row.id}`)}
                >
                  <td className="px-4 py-2 font-medium">{row.name}</td>
                  <td className="px-4 py-2 text-text-secondary font-mono">{row.kind}</td>
                  <td className="px-4 py-2">
                    <span className={`inline-block px-2 py-0.5 rounded ${STATUS_BADGE[row.status]}`}>
                      {STATUS_LABEL[row.status]}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-text-secondary font-mono">
                    {row.site_id?.slice(0, 12) ?? "—"}
                    {row.site_id && row.site_id.length > 12 && "…"}
                  </td>
                  <td className="px-4 py-2 text-text-muted">
                    {new Date(row.created_at).toLocaleString("zh-TW", {
                      timeZone: "Asia/Taipei",
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
